import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Button, ButtonGroup, Dropdown, Table } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'
import DeleteIcon from '../../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../../components/AllSvgIcon/EditIcon'
import BasicFilterComponents from '../../../../components/BasicFilterComponents'
import Loader from '../../../../components/Loader'
import RecordNotFound from '../../../../components/RecordNotFound'
import CustomToast from '../../../../components/Toast/CustomToast'
import { encodedSearchText, showToast } from '../../../../lib/AllGlobalFunction'
import {
  allCrudNames,
  allPages,
  checkPageAccess
} from '../../../../lib/AllPageNames'
import { pageRangeDisplayed } from '../../../../lib/AllStaticVariables'
import axiosProvider from '../../../../lib/AxiosProvider'
import { _SwalDelete, _exception } from '../../../../lib/exceptionMessage'
import useDebounce from '../../../../lib/useDebounce'
import HSNBulkUpload from './HSNBulkUpload'

const HSNCodeForm = lazy(() => import('./HSNCodeForm'))

const HSNCodeList = () => {
  const initVal = {
    hsnCode: '',
    description: ''
  }
  const [searchText, setSearchText] = useState()
  const [data, setData] = useState()
  const [modalShow, setModalShow] = useState({ show: false, type: '' })
  const [loading, setLoading] = useState(false)
  const [initialValues, setInitialValues] = useState(initVal)
  const { userId } = useSelector((state) => state?.user?.userInfo)
  const location = useLocation()
  const { pageAccess } = useSelector((state) => state.user)
  const debounceSearchText = useDebounce(searchText, 500)
  const [filterDetails, setFilterDetails] = useImmer({
    pageSize: 50,
    pageIndex: 1,
    searchText: ''
  })
  const [toast, setToast] = useState({
    show: false,
    text: null,
    variation: null
  })

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: `HSNCode?Id=${id}`,
        userId,
        location: location.pathname
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        fetchData()
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)

      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'HSNCode/search',
        queryString: `?searchText=${encodedSearchText(
          filterDetails?.searchText
        )}&pageIndex=${filterDetails?.pageIndex}&pageSize=${
          filterDetails?.pageSize
        }`
      })
      setLoading(false)
      if (response?.status === 200) {
        setData(response)
      }
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const handlePageClick = (event) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    setFilterDetails((draft) => {
      draft.pageIndex = event.selected + 1
    })
  }

  useEffect(() => {
    fetchData()
  }, [filterDetails])

  useEffect(() => {
    if (debounceSearchText) {
      setFilterDetails((draft) => {
        draft.searchText = debounceSearchText
        draft.pageIndex = 1
      })
    } else {
      setFilterDetails((draft) => {
        draft.searchText = ''
        draft.pageIndex = 1
      })
    }
  }, [debounceSearchText])

  return (
    <>
      <div className='d-flex align-items-center mb-3 gap-3 justify-content-between'>
        {data && (
          <BasicFilterComponents
            data={data}
            filterDetails={filterDetails}
            setFilterDetails={setFilterDetails}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        )}

        {checkPageAccess(
          pageAccess,
          allPages?.hsnCode,
          allCrudNames?.write
        ) && (
          <Dropdown className='ms-auto' as={ButtonGroup}>
            <Button
              variant='warning'
              className='fw-600 d-flex align-items-center gap-2'
              onClick={() => {
                setInitialValues(initVal)
                setModalShow({ show: true, type: 'form' })
              }}
            >
              <i className='m-icon m-icon--plusblack'></i>
              Create
            </Button>
            <Dropdown.Toggle
              split
              variant='warning'
              id='dropdown-split-basic'
            />

            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() =>
                  setModalShow({
                    show: true,
                    type: 'bulkUpload'
                  })
                }
              >
                <span>Bulk Upload</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      {data && (
        <Table responsive className='align-middle table-list'>
          <thead>
            <tr>
              <th>HSN Code</th>
              <th>Description</th>
              {checkPageAccess(pageAccess, allPages?.hsnCode, [
                allCrudNames?.update,
                allCrudNames?.delete
              ]) && <th className='text-center'>Action</th>}
            </tr>
          </thead>
          <tbody>
            {data?.data?.data?.length > 0 ? (
              data?.data?.data?.map((data, index) => (
                <tr key={data.id}>
                  <td>{data.hsnCode}</td>
                  <td>{data.description}</td>
                  {checkPageAccess(pageAccess, allPages?.hsnCode, [
                    allCrudNames?.update,
                    allCrudNames?.delete
                  ]) && (
                    <td className='text-center'>
                      <div className='d-flex gap-2 justify-content-center'>
                        {checkPageAccess(
                          pageAccess,
                          allPages?.hsnCode,
                          allCrudNames?.update
                        ) && (
                          <span
                            onClick={() => {
                              setInitialValues(data)
                              setModalShow({ show: true, type: 'form' })
                            }}
                          >
                            <EditIcon bg={'bg'} />
                          </span>
                        )}
                        {checkPageAccess(
                          pageAccess,
                          allPages?.hsnCode,
                          allCrudNames?.delete
                        ) && (
                          <span
                            onClick={() => {
                              Swal.fire({
                                title: _SwalDelete.title,
                                text: _SwalDelete.text,
                                icon: _SwalDelete.icon,
                                showCancelButton: _SwalDelete.showCancelButton,
                                confirmButtonColor:
                                  _SwalDelete.confirmButtonColor,
                                cancelButtonColor:
                                  _SwalDelete.cancelButtonColor,
                                confirmButtonText:
                                  _SwalDelete.confirmButtonText,
                                cancelButtonText: _SwalDelete.cancelButtonText
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  handleDelete(data?.id)
                                } else if (result.isDenied) {
                                }
                              })
                            }}
                          >
                            <DeleteIcon bg={'bg'} />
                          </span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className='text-center'>
                  {data?.data?.message}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {data?.data?.pagination?.pageCount > 0 && (
        <ReactPaginate
          className='list-inline m-cst--pagination d-flex justify-content-end gap-1'
          breakLabel='...'
          nextLabel=''
          onPageChange={handlePageClick}
          pageRangeDisplayed={pageRangeDisplayed}
          pageCount={data?.data?.pagination?.pageCount}
          previousLabel=''
          renderOnZeroPageCount={null}
          forcePage={filterDetails?.pageIndex - 1}
        />
      )}

      <Suspense fallback={<Loader />}>
        {modalShow?.show && modalShow?.type === 'form' && (
          <HSNCodeForm
            modalShow={modalShow}
            setModalShow={setModalShow}
            initialValues={initialValues}
            loading={loading}
            setLoading={setLoading}
            fetchData={fetchData}
            toast={toast}
            setToast={setToast}
          />
        )}

        {modalShow?.show && modalShow?.type === 'bulkUpload' && (
          <HSNBulkUpload
            modalShow={modalShow}
            setModalShow={setModalShow}
            loading={loading}
            setLoading={setLoading}
            fetchData={fetchData}
            toast={toast}
            setToast={setToast}
          />
        )}
      </Suspense>

      {!loading &&
        !filterDetails?.searchText &&
        !data?.data?.pagination?.pageCount && <RecordNotFound />}

      {loading && !modalShow?.show && <Loader />}

      {toast?.show && !modalShow?.show && (
        <CustomToast text={toast?.text} variation={toast?.variation} />
      )}
    </>
  )
}

export default HSNCodeList
